import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";

const API_URL = `${getAPIbaseURL()}/documents`;

const authConfig = (extra = {}) => ({
  ...extra,
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    ...extra.headers,
  },
});

const errorValue = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.error || fallback;

export const fetchExplorer = createAsyncThunk(
  "documentLibrary/fetchExplorer",
  async (parentId, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/explorer`, authConfig({
        params: parentId ? { parent_id: parentId } : {},
      }));
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load documents"));
    }
  },
);

export const fetchCollaborators = createAsyncThunk(
  "documentLibrary/fetchCollaborators",
  async (_, thunkAPI) => {
    try {
      return (await axios.get(`${API_URL}/collaborators`, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load people"));
    }
  },
);

export const createLibraryFolder = createAsyncThunk(
  "documentLibrary/createFolder",
  async (payload, thunkAPI) => {
    try {
      return (await axios.post(`${API_URL}/folders`, payload, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to create folder"));
    }
  },
);

export const updateLibraryFolder = createAsyncThunk(
  "documentLibrary/updateFolder",
  async ({ folderId, updates }, thunkAPI) => {
    try {
      return (await axios.patch(`${API_URL}/folders/${folderId}`, updates, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to update folder"));
    }
  },
);

export const replaceFolderPermissions = createAsyncThunk(
  "documentLibrary/replacePermissions",
  async ({ folderId, permissions }, thunkAPI) => {
    try {
      return (await axios.put(
        `${API_URL}/folders/${folderId}/permissions`,
        permissions,
        authConfig(),
      )).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to update access"));
    }
  },
);

export const uploadFolderArtwork = createAsyncThunk(
  "documentLibrary/uploadArtwork",
  async ({ folderId, image }, thunkAPI) => {
    const body = new FormData();
    body.append("image", image);
    try {
      return (await axios.post(`${API_URL}/folders/${folderId}/image`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to update folder image"));
    }
  },
);

export const deleteLibraryFolder = createAsyncThunk(
  "documentLibrary/deleteFolder",
  async (folderId, thunkAPI) => {
    try {
      await axios.delete(`${API_URL}/folders/${folderId}`, authConfig());
      return folderId;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to delete folder"));
    }
  },
);

export const uploadLibraryDocument = createAsyncThunk(
  "documentLibrary/uploadDocument",
  async ({ folderId, file, documentType = "Other" }, thunkAPI) => {
    const body = new FormData();
    body.append("title", file.name);
    body.append("folder_id", folderId);
    body.append("document_type", documentType);
    body.append("file", file);
    try {
      return (await axios.post(`${API_URL}/`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to upload file"));
    }
  },
);

export const uploadDocumentVersion = createAsyncThunk(
  "documentLibrary/uploadVersion",
  async ({ documentId, file }, thunkAPI) => {
    const body = new FormData();
    body.append("file", file);
    try {
      return (await axios.post(`${API_URL}/${documentId}/versions`, body, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to upload version"));
    }
  },
);

export const fetchDocumentVersions = createAsyncThunk(
  "documentLibrary/fetchVersions",
  async (documentId, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/${documentId}/versions`, authConfig());
      return { documentId, versions: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to load version history"));
    }
  },
);

export const renameLibraryDocument = createAsyncThunk(
  "documentLibrary/renameDocument",
  async ({ documentId, title }, thunkAPI) => {
    try {
      return (await axios.patch(`${API_URL}/${documentId}`, { title }, authConfig())).data;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to rename file"));
    }
  },
);

export const deleteLibraryDocument = createAsyncThunk(
  "documentLibrary/deleteDocument",
  async (documentId, thunkAPI) => {
    try {
      await axios.delete(`${API_URL}/${documentId}`, authConfig());
      return documentId;
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to delete file"));
    }
  },
);

export const downloadLibraryDocument = createAsyncThunk(
  "documentLibrary/downloadDocument",
  async ({ documentId, versionId }, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/${documentId}/download`, authConfig({
        params: versionId ? { version_id: versionId } : {},
        responseType: "blob",
      }));
      const disposition = response.headers["content-disposition"] || "";
      const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1];
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = encoded ? decodeURIComponent(encoded) : "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      return thunkAPI.rejectWithValue(errorValue(error, "Unable to download file"));
    }
  },
);

const initialState = {
  currentFolder: null,
  breadcrumbs: [],
  folders: [],
  documents: [],
  collaborators: [],
  versionsByDocument: {},
  isLoading: false,
  error: null,
};

const documentLibrarySlice = createSlice({
  name: "documentLibrary",
  initialState,
  reducers: {
    clearDocumentError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExplorer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExplorer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentFolder = action.payload.current_folder;
        state.breadcrumbs = action.payload.breadcrumbs;
        state.folders = action.payload.folders;
        state.documents = action.payload.documents;
      })
      .addCase(fetchExplorer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCollaborators.fulfilled, (state, action) => {
        state.collaborators = action.payload;
      })
      .addCase(fetchDocumentVersions.fulfilled, (state, action) => {
        state.versionsByDocument[action.payload.documentId] = action.payload.versions;
      })
      .addMatcher(
        (action) => action.type.startsWith("documentLibrary/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.error = action.payload;
        },
      );
  },
});

export const { clearDocumentError } = documentLibrarySlice.actions;
export default documentLibrarySlice.reducer;
